/**
 * Centralized navigation hooks to avoid circular dependencies
 * Components should import from this file instead of importing route configs that import components
 */
import {
  useNavigate,
  useLocation,
  useParams,
  NavigateOptions,
} from 'react-router-dom';
import { createSearchParams } from 'react-router-dom';

// Define path constants locally to avoid circular dependencies
const DOCKET_PREFIX_PATH = '/docket';
const MEMBER_LIST_PREFIX_PATH = '/member-list';

export const getMemberPagePath = (
  currentPathname: string,
  guideId: string,
  memberId: string
) => {
  let basePath = MEMBER_LIST_PREFIX_PATH;
  if (currentPathname.startsWith(DOCKET_PREFIX_PATH)) {
    basePath = DOCKET_PREFIX_PATH;
  }

  return `${basePath}/guide/${guideId || 'none'}/member/${memberId}`;
};

export const getMemberPageUrl = (
  currentPathname: string,
  guideId: string,
  memberId: string
) => {
  const path = getMemberPagePath(currentPathname, guideId, memberId);
  const host = window.location.host;
  const protocol = window.location.protocol;
  return `${protocol}//${host}${path}`;
};

export const useNavigateToDocket = () => {
  const navigate = useNavigate();
  const { guideId: routeGuideId } = useParams();

  return (guideId?: string, options?: NavigateOptions) => {
    if (guideId && guideId !== 'none') {
      navigate(`${DOCKET_PREFIX_PATH}/guide/${guideId}`, options);
    } else if (routeGuideId && routeGuideId !== 'none') {
      navigate(`${DOCKET_PREFIX_PATH}/guide/${routeGuideId}`, options);
    } else {
      navigate(DOCKET_PREFIX_PATH, options);
    }
  };
};

export const useNavigateToMemberPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    guideId: string,
    memberId: string,
    feedItem?: {
      externalType?: string;
      externalId?: string | number;
    },
    needId?: string,
    replace?: boolean,
    showGoals?: boolean,
    noteId?: string
  ) => {
    const {
      externalType: feedItemExternalType,
      externalId: feedItemExternalId,
    } = feedItem || {};

    const url = getMemberPagePath(location.pathname, guideId, memberId);

    const params: Record<string, string> = {};
    if (feedItemExternalType)
      params.feedItemExternalType = feedItemExternalType;
    if (feedItemExternalId)
      params.feedItemExternalId = feedItemExternalId.toString();
    if (needId) params.needId = needId;
    if (showGoals) params.showGoals = 'true';
    if (noteId) params.note = noteId;

    const queryString =
      Object.keys(params).length > 0 ? `?${createSearchParams(params)}` : '';

    navigate(`${url}${queryString}`, {
      replace: !!replace,
    });
  };
};

export const useNavigateToCalls = () => {
  const navigate = useNavigate();
  return (filters?: { onlyUnassignedCalls?: boolean }) => {
    if (filters?.onlyUnassignedCalls) {
      // Update the callsFilterConfig in localStorage
      const existingConfig = window.localStorage.getItem('callsFilterConfig');
      const config = existingConfig ? JSON.parse(existingConfig) : {};
      config.onlyUnassignedCalls = true;
      window.localStorage.setItem('callsFilterConfig', JSON.stringify(config));
    }
    navigate('/calls');
  };
};
